<template>
  <NavBar v-if="userLoaded" pageName="Notification Templates"></NavBar>

  <v-container style="margin-top: 185px;">
    <!-- Loading Overlay -->
    <v-overlay :model-value="loading" class="align-center justify-center">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <v-row class="align-center mb-4">
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <v-btn icon variant="text" size="small" @click="$router.push('/settings/notifications')" class="mr-2">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <div>
            <h2>Notification Templates</h2>
            <p class="text-caption text-grey">Configure notification and email templates for system events</p>
          </div>
        </div>
      </v-col>
      <v-col cols="12" md="6" class="d-flex justify-end gap-2">
        <v-btn color="primary" variant="outlined" :loading="loading" @click="loadData">
          <v-icon class="mr-2">mdi-refresh</v-icon>
          Refresh
        </v-btn>
        <v-btn color="success" @click="openAddDialog">
          <v-icon class="mr-2">mdi-plus</v-icon>
          Add Template
        </v-btn>
      </v-col>
    </v-row>

    <!-- Filter by category -->
    <v-row class="mb-2">
      <v-col cols="12" md="4">
        <v-select
          v-model="filterCategory"
          :items="categoryOptions"
          label="Filter by Category"
          density="compact"
          clearable
          hide-details
        />
      </v-col>
    </v-row>

    <v-card>
      <v-card-text>
        <v-data-table
          :headers="tableHeaders"
          :items="filteredTemplates"
          :loading="loading"
          :items-per-page="20"
          item-value="id"
          class="elevation-1"
          show-expand
        >
          <template #[`item.event_category`]="{ item }">
            <v-chip
              :color="categoryColor(item.event_category)"
              size="small"
              variant="tonal"
            >
              {{ item.event_category }}
            </v-chip>
          </template>
          <template #[`item.active`]="{ item }">
            <v-chip
              :color="item.active ? 'success' : 'error'"
              size="small"
              @click="toggleActive(item)"
              style="cursor: pointer;"
            >
              {{ item.active ? 'Active' : 'Inactive' }}
            </v-chip>
          </template>
          <template #[`item.actions`]="{ item }">
            <v-btn size="x-small" color="primary" class="mr-1" @click="openEditDialog(item)">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn size="x-small" color="error" @click="confirmDelete(item)">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>

          <!-- Expanded row: preview templates -->
          <template v-slot:expanded-row="{ columns, item }">
            <tr>
              <td :colspan="columns.length" class="pa-4" style="background: #f5f5f5;">
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="text-subtitle-2 mb-1">In-App Notification</div>
                    <div class="text-caption"><strong>Subject:</strong> {{ item.notification_subject }}</div>
                    <div class="text-caption mt-1" style="white-space: pre-wrap;"><strong>Body:</strong> {{ item.notification_body }}</div>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="text-subtitle-2 mb-1">Email</div>
                    <div class="text-caption"><strong>Subject:</strong> {{ item.email_subject }}</div>
                    <div class="text-caption mt-1" style="white-space: pre-wrap;"><strong>Body:</strong> {{ item.email_body }}</div>
                  </v-col>
                </v-row>
                <div class="mt-2" v-if="item.available_variables">
                  <div class="text-caption text-grey">Variables: {{ item.available_variables }}</div>
                </div>
              </td>
            </tr>
          </template>

          <template v-slot:no-data>
            <v-alert type="info" class="ma-2">No notification templates configured</v-alert>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Add/Edit Dialog -->
    <NotificationTemplateDialog
      :show="formDialog"
      :is-editing="isEditing"
      :form-data="formData"
      :saving="saving"
      @close="formDialog = false"
      @save="saveTemplate"
    />

    <!-- Delete Confirmation -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="bg-error text-white">
          <v-icon class="mr-2">mdi-delete</v-icon>
          Confirm Delete
        </v-card-title>
        <v-card-text class="pt-4">
          Are you sure you want to delete this notification template?
          <div class="mt-2" v-if="templateToDelete">
            <strong>Event:</strong> {{ templateToDelete.event_label }}<br>
            <strong>Name:</strong> {{ templateToDelete.event_name }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="deleteTemplate" :loading="deleting">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import NavBar from "@/components/NavBar.vue";
import NotificationTemplateDialog from "@/components/dialogs/NotificationTemplateDialog.vue";
import useGetLoggedIn from "@/composables/useGetLoggedIn";
import {
  getNotificationTemplates,
  addNotificationTemplate,
  updateNotificationTemplate,
  deleteNotificationTemplate
} from "@/composables/useNotificationTemplatesAPI";
import { log } from "@/composables/useLog";
import store from "@/store";

export default defineComponent({
  name: 'NotificationTemplatesSettings',
  components: {
    NavBar,
    NotificationTemplateDialog
  },
  data() {
    return {
      loading: false,
      token: '',
      userLoaded: false,
      templates: [],
      filterCategory: null,

      // Dialog
      formDialog: false,
      isEditing: false,
      saving: false,
      formData: {
        id: null, event_name: '', event_category: '', event_label: '',
        notification_subject: '', notification_body: '',
        email_subject: '', email_body: '',
        available_variables: '', active: true
      },

      // Delete
      deleteDialog: false,
      deleting: false,
      templateToDelete: null,

      // Snackbar
      snackbar: { show: false, message: '', color: 'success' },

      // Table
      tableHeaders: [
        { title: 'ID', key: 'id', width: '5%' },
        { title: 'Event Label', key: 'event_label', width: '25%' },
        { title: 'Event Name', key: 'event_name', width: '25%' },
        { title: 'Category', key: 'event_category', width: '15%' },
        { title: 'Status', key: 'active', align: 'center', width: '10%' },
        { title: 'Actions', key: 'actions', sortable: false, width: '12%' }
      ],
      categoryOptions: ['quarantine', 'termination', 'renewal', 'billing', 'general']
    }
  },
  computed: {
    filteredTemplates() {
      if (!this.filterCategory) return this.templates;
      return this.templates.filter(t => t.event_category === this.filterCategory);
    }
  },
  methods: {
    showSnackbar(message, color = 'success') {
      this.snackbar = { show: true, message, color };
    },

    categoryColor(category) {
      const colors = {
        quarantine: 'orange',
        termination: 'red',
        renewal: 'green',
        billing: 'blue',
        general: 'grey'
      };
      return colors[category] || 'grey';
    },

    async loadData() {
      this.loading = true;
      try {
        const result = await getNotificationTemplates(this.token);
        if (!result.error) {
          this.templates = result.templates || [];
        } else {
          this.showSnackbar(result.message || 'Error loading data', 'error');
        }
      } catch (err) {
        log("Error loading notification templates:", err);
        this.showSnackbar('Error loading data', 'error');
      } finally {
        this.loading = false;
      }
    },

    openAddDialog() {
      this.isEditing = false;
      this.formData = {
        id: null, event_name: '', event_category: '', event_label: '',
        notification_subject: '', notification_body: '',
        email_subject: '', email_body: '',
        available_variables: '{client_name}, {service_name}, {cs_id}, {msisdn_number}, {imsi_number}, {sim_serial}, {expiration_date}, {quarantine_days}, {quarantine_expires_at}, {termination_reason}',
        active: true
      };
      this.formDialog = true;
    },

    openEditDialog(item) {
      this.isEditing = true;
      this.formData = { ...item };
      this.formDialog = true;
    },

    async saveTemplate(data) {
      this.saving = true;
      try {
        let result;
        if (this.isEditing) {
          result = await updateNotificationTemplate(this.token, data);
        } else {
          result = await addNotificationTemplate(this.token, data);
        }
        if (!result.error) {
          this.showSnackbar(this.isEditing ? 'Template updated' : 'Template added');
          this.formDialog = false;
          await this.loadData();
        } else {
          this.showSnackbar(result.message || 'Error saving', 'error');
        }
      } catch (err) {
        this.showSnackbar('Error saving', 'error');
      } finally {
        this.saving = false;
      }
    },

    async toggleActive(item) {
      this.loading = true;
      try {
        const result = await updateNotificationTemplate(this.token, { id: item.id, active: !item.active });
        if (!result.error) {
          this.showSnackbar(`Template ${!item.active ? 'activated' : 'deactivated'}`);
          await this.loadData();
        } else {
          this.showSnackbar(result.message || 'Error updating', 'error');
        }
      } catch (err) {
        this.showSnackbar('Error updating', 'error');
      } finally {
        this.loading = false;
      }
    },

    confirmDelete(item) {
      this.templateToDelete = item;
      this.deleteDialog = true;
    },

    async deleteTemplate() {
      if (!this.templateToDelete) return;
      this.deleting = true;
      try {
        const result = await deleteNotificationTemplate(this.token, this.templateToDelete.id);
        if (!result.error) {
          this.showSnackbar('Template deleted');
          this.deleteDialog = false;
          this.templateToDelete = null;
          await this.loadData();
        } else {
          this.showSnackbar(result.message || 'Error deleting', 'error');
        }
      } catch (err) {
        this.showSnackbar('Error deleting', 'error');
      } finally {
        this.deleting = false;
      }
    }
  },

  async created() {
    const { getLoggedIn } = useGetLoggedIn();
    await getLoggedIn();
    this.token = store.getters.getToken;
    this.userLoaded = true;
    await this.loadData();
  }
});
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}
</style>
